// ============================================================================
// CASE NUMBER FORMAT MASTER SCREEN — CFG-CASENO-001
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useCaseNumberFormats } from 'hooks/api/useCaseNumberFormats';
import { useCaseTypeOptions } from 'hooks/api/useCaseTypes';
import { caseNumberFormatService } from 'api/services/caseNumberFormatService';
import type { CaseNumberFormat, CaseNumberFormatSearchRequest } from 'api/types/caseNumberFormat.types';
import CaseNumberFormatFormModal from 'components/modals/modules/case-number-format-module/CaseNumberFormatFormModal';

type ViewMode = 'list' | 'grid';

interface CaseNumberFormatMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const CADENCE_LABELS: Record<string, string> = {
  NEVER: 'Never',
  YEARLY: 'Yearly',
  MONTHLY: 'Monthly',
  FY_INDIA: 'India FY',
};

const CaseNumberFormatMasterScreen: React.FC<CaseNumberFormatMasterScreenProps> = ({ tabId, moduleName }) => {
  // Case-type display labels + filter options come from mst.tbl_case_type
  // (single source of truth) — no hardcoded enum.
  const { options: caseTypeOptions, labels: CASE_TYPE_LABELS } = useCaseTypeOptions();
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<CaseNumberFormatSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, loading, pagination, refetch } = useCaseNumberFormats(queryParams);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [formatCodeFilter, setFormatCodeFilter] = useState('');
  const [formatNameFilter, setFormatNameFilter] = useState('');
  const [caseTypeFilter, setCaseTypeFilter] = useState<string>('');
  const [cadenceFilter, setCadenceFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery.trim() || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selectedFmt, setSelectedFmt] = useState<CaseNumberFormat | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(r => r.formatCode === recordCode);
        if (rec) setSelectedFmt(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fmtToDelete, setFmtToDelete] = useState<CaseNumberFormat | null>(null);

  const handleAddNew = useCallback(() => {
    if (formModalOpen) formModalMinimizeRef.current?.();
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedFmt(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback(
    (f: CaseNumberFormat) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${f.formatCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedFmt(f);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleView = useCallback(
    (f: CaseNumberFormat) => {
      const modalId = tabId ? `${tabId}-view-modal-${f.formatCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedFmt(f);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (f: CaseNumberFormat) => {
      setSelectedFmt(f);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((f: CaseNumberFormat) => {
    setFmtToDelete(f);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!fmtToDelete) return;
    setIsDeleting(true);
    try {
      await caseNumberFormatService.delete(fmtToDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Format "${fmtToDelete.formatCode}" deleted successfully.`);
      setFmtToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [fmtToDelete, refetch]);

  const handleSaved = useCallback(() => { refetch(); }, [refetch]);
  const handleRefresh = useCallback(() => { refetch(); }, [refetch]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      formatCode: formatCodeFilter.trim() || undefined,
      formatName: formatNameFilter.trim() || undefined,
      caseTypeCode: caseTypeFilter || undefined,
      resetCadence: cadenceFilter || undefined,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [formatCodeFilter, formatNameFilter, caseTypeFilter, cadenceFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setFormatCodeFilter('');
    setFormatNameFilter('');
    setCaseTypeFilter('');
    setCadenceFilter('');
    setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.formatCode) count++;
    if (queryParams.formatName) count++;
    if (queryParams.caseTypeCode) count++;
    if (queryParams.resetCadence) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams]);

  const formatDate = (s?: string | null) => {
    if (!s) return '-';
    try { return new Date(s).toLocaleDateString(); } catch { return s; }
  };

  const columns: SyutGridColumn<CaseNumberFormat>[] = useMemo(() => [
    { id: 'formatCode', header: 'Code', accessorKey: 'formatCode', size: 160, enableSorting: true },
    { id: 'formatName', header: 'Name', accessorKey: 'formatName', size: 240, enableSorting: true, flex: true },
    { id: 'caseTypeCode', header: 'Case Type', accessorKey: 'caseTypeCode', size: 140, enableSorting: true,
      cell: ({ row }) => CASE_TYPE_LABELS[row.original.caseTypeCode] || row.original.caseTypeCode },
    { id: 'prefix', header: 'Prefix', accessorKey: 'prefix', size: 90 },
    { id: 'infixPattern', header: 'Pattern', accessorKey: 'infixPattern', size: 200,
      cell: ({ row }) => <code style={{ fontSize: '0.75rem' }}>{row.original.infixPattern || '-'}</code> },
    { id: 'sequencePadding', header: 'Pad', accessorKey: 'sequencePadding', size: 70, enableSorting: true },
    { id: 'resetCadence', header: 'Reset', accessorKey: 'resetCadence', size: 110, enableSorting: true,
      cell: ({ row }) => CADENCE_LABELS[row.original.resetCadence] || row.original.resetCadence },
    { id: 'lastSequenceValue', header: 'Last #', accessorKey: 'lastSequenceValue', size: 100,
      cell: ({ row }) => row.original.lastSequenceValue.toLocaleString() },
    { id: 'effectiveFrom', header: 'From', accessorKey: 'effectiveFrom', size: 110, enableSorting: true,
      cell: ({ row }) => formatDate(row.original.effectiveFrom) },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => {
        const c = row.original;
        const code = c.deletedAt ? 'DELETED' : (c.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span style={{
            fontSize: '0.75rem', fontWeight: 600, color,
            padding: '2px 8px', border: `1px solid ${color}`, borderRadius: '999px',
          }} title={c.deletedAt ? `Deleted at ${new Date(c.deletedAt).toLocaleString()}` : undefined}>
            {code}
          </span>
        );
      },
    },
  ], []);

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  const getRowActions = useCallback((f: CaseNumberFormat): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(f) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(f) },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(f) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(f), variant: 'danger' },
  ], [handleEdit, handleView, handleCopy, handleDeleteClick]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add', tooltip: 'Add New Format', onClick: handleAddNew, variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', tooltip: 'Refresh', onClick: handleRefresh, variant: 'icon' },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Case Number Format Master"
        description="Per-case-type numbering templates with prefix, infix pattern, sequence padding, and reset cadence. Drives case-number allocation in the Litigation Engine."
        icon={<IconSystem.tag size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, case type, prefix...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (m) => setViewMode(m as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No case number formats found"
              />
            )}

            {viewMode === 'grid' && (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  padding: '1rem', display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
                }}>
                  {data.map((f) => (
                    <div key={String(f.id)} style={{
                      position: 'relative',
                      border: '1px solid var(--border-color)', borderRadius: '8px',
                      padding: '1rem', background: 'var(--bg-primary)', cursor: 'pointer',
                    }} onClick={() => handleEdit(f)}>
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(f)} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, paddingRight: '1.5rem' }}>
                        {f.formatCode} · {CASE_TYPE_LABELS[f.caseTypeCode] || f.caseTypeCode}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>
                        {f.formatName}
                      </div>
                      <div style={{ fontSize: '0.8125rem', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
                        <code>{f.infixPattern || '-'}</code>
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                        Reset: {CADENCE_LABELS[f.resetCadence]} · Last: {f.lastSequenceValue.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: f.statusCode === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                        {f.statusCode || 'ACTIVE'}
                      </div>
                    </div>
                  ))}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={queryParams.pageSize ?? 25}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                        <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange((queryParams.page ?? 1) - 2)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>
                        Page {queryParams.page ?? 1} of {Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(queryParams.page ?? 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25))) - 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                        <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Format Code</label>
              <input type="text" className="form-input" value={formatCodeFilter}
                onChange={(e) => setFormatCodeFilter(e.target.value)} placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Format Name</label>
              <input type="text" className="form-input" value={formatNameFilter}
                onChange={(e) => setFormatNameFilter(e.target.value)} placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Case Type"
                options={[{ value: '', label: 'All case types' }, ...caseTypeOptions]}
                value={caseTypeFilter}
                onChange={(v) => setCaseTypeFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Reset Cadence"
                options={[
                  { value: '', label: 'All cadences' },
                  { value: 'NEVER', label: 'Never' },
                  { value: 'YEARLY', label: 'Yearly' },
                  { value: 'MONTHLY', label: 'Monthly' },
                  { value: 'FY_INDIA', label: 'India FY' },
                ]}
                value={cadenceFilter}
                onChange={(v) => setCadenceFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED', label: 'Deleted only' },
                  { value: 'ALL', label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={fmtToDelete ? `Delete format "${fmtToDelete.formatCode}"? Existing case numbers already issued under this format remain valid; only future allocations are affected.` : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) { setDeleteModalOpen(false); setFmtToDelete(null); }
        }}
      />

      <CaseNumberFormatFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedFmt}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default CaseNumberFormatMasterScreen;
